import {
  HttpClient,
  HttpEventType,
  type HttpInterceptorFn
} from '@angular/common/http';
import { computed, inject } from '@angular/core';
import type { Page } from '@app/admin/model/spring-pageable';
import { type AppConfig, appConfigService } from '@app/config';
import {
  type QueryClient,
  injectQuery,
  injectQueryClient
} from '@tanstack/angular-query-experimental';
import { type Observable, lastValueFrom, tap } from 'rxjs';

type PrefetchableQueryOptions<T, K> = {
  queryKey: (string | number)[];
  id?: K;
  staleTime?: number;
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
  query: (options: {
    httpClient: HttpClient;
    config: AppConfig;
    id?: K;
  }) => Observable<T>;
};

const injectPrefetchableQuery = <T, K>(
  params: () => PrefetchableQueryOptions<T, K>
) => {
  const httpClient = inject(HttpClient);
  const config = inject(appConfigService);
  const queryClient = injectQueryClient();

  const options = (id?: K) => ({
    queryKey: [...params().queryKey, params().id],
    staleTime: params().staleTime ?? 10_000,
    enabled: params().enabled ?? true,
    refetchOnWindowFocus: params().refetchOnWindowFocus ?? true,
    keepPreviousData: false,
    queryFn: () => {
      return lastValueFrom(
        params().query({
          httpClient,
          config,
          id: id
        })
      );
    }
  });

  const query = injectQuery(() => options(params().id));

  const prefetchQuery = (id: K) => {
    return queryClient.prefetchQuery(options(id));
  };

  const setQueryData = (id: K, data: T) => {
    return queryClient.setQueryData(options(id).queryKey, data);
  };

  return {
    query,
    prefetchQuery,
    setQueryData
  };
};

type HierarchyLevelParam = { id: number | string; page: number };
const hierarchyLevelQueryOptions = ({
  id,
  page,
  enabled
}: HierarchyLevelParam & { enabled?: boolean }) =>
  ({
    queryKey: ['hierarchy', 'hierarchy-level'],
    id: {
      id: id,
      page: page
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: enabled ?? true,
    query: ({ id, config, httpClient }) => {
      return httpClient.get<Page<HierarchyNode>>(
        `${config.apiHost}hierarchy/level/${id?.id}?page=${id?.page}`
      );
    }
  } satisfies PrefetchableQueryOptions<unknown, HierarchyLevelParam>);

export const injectHierarchyLevel = (
  paramsFactory: () => HierarchyLevelParam & { enabled?: boolean }
) => {
  const query = injectPrefetchableQuery(() =>
    hierarchyLevelQueryOptions({
      id: paramsFactory().id,
      page: paramsFactory().page
    })
  );

  return query;
};

const setHierarchyLevelQueryData = (
  data: Page<HierarchyNode>,
  id: HierarchyLevelParam,

  queryClient: QueryClient
) => {
  const queryOptions = hierarchyLevelQueryOptions(id);
  queryClient.setQueryData([...queryOptions.queryKey, id], data);
};

type InitialHierarchyParams = { id: number | string };
export const injectInitialHierarchy = (
  paramsFactory: () => InitialHierarchyParams
) => {
  const id = computed(() => paramsFactory().id);
  const queryClient = injectQueryClient();

  return injectPrefetchableQuery(() => {
    return fetchHierarchyInitialOptions({ id: id() }, queryClient);
  });
};

export const fetchHierarchyInitialOptions = (
  param: InitialHierarchyParams,
  queryClient: QueryClient
) =>
  ({
    id: param,
    queryKey: ['hierarchy', 'hierarchy-single'],
    query: ({ id, config, httpClient }) => {
      return httpClient
        .get<{
          breadcrumbs: HierarchyPosition[];
          hierarchy: Page<HierarchyNode>;
        }>(`${config.apiHost}hierarchy/${id?.id}`)
        .pipe(
          tap((data) => {
            for (const node of data.hierarchy.content) {
              prepopulateHierarchyLevel(node, queryClient);
            }
          })
        );
    }
  } satisfies PrefetchableQueryOptions<unknown, InitialHierarchyParams>);

const prepopulateHierarchyLevel = (
  node: HierarchyNode,
  queryClient: QueryClient
) => {
  if (node.children && node.children.content.length > 0) {
    setHierarchyLevelQueryData(
      node.children,
      {
        id: node.id,
        page: node.children.pageable.pageNumber
      },
      queryClient
    );
    for (const child of node.children.content) {
      prepopulateHierarchyLevel(child, queryClient);
    }
  }
};

export type HierarchyPosition = {
  id: number;
  arhivskaOznaka: string;
  title: string;
  hasChildren: boolean;
  isTarget: boolean;
  razina: number;
  rezidualniPodaci: boolean;
};
export type HierarchyNode = HierarchyPosition & {
  children: Page<HierarchyNode>;
};

export const revalidateHierarchyInterceptor: HttpInterceptorFn = (
  req,
  next
) => {
  const queryClient = injectQueryClient();

  const request = req.clone();
  const requestBody = request.body;
  const shouldRevalidate =
    (request.url.includes('executeAction') ||
      request.url.includes('saveViewData')) &&
    '$itemType' in (requestBody as Record<string, unknown>) &&
    (requestBody as Record<string, unknown>).$itemType ===
      'en~jedinica_gradiva';

  return next(req).pipe(
    tap((event) => {
      if (event.type === HttpEventType.Response && shouldRevalidate) {
        queryClient.invalidateQueries({
          queryKey: ['hierarchy']
        });
      }
    })
  );
};
